import styles from './PositionPicker.module.scss';

import { Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Position } from '@work4all/models/lib/Classes/Position.entity';
import { ServiceContractPosition } from '@work4all/models/lib/Classes/ServiceContractPosition.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';
import { ServiceContractPositionKind } from '@work4all/models/lib/Enums/ServiceContractPositionKind.enum';

import { HeaderRow } from '../components/custom-list-entity-picker/HeaderRow';
import { ItemRow } from '../components/custom-list-entity-picker/ItemRow';
import { Picker } from '../components/v2/Picker';
import { usePickerContext } from '../components/v2/picker-context';
import { IPickerProps } from '../types';

import {
  PositionPickerSettings,
  PositionPickerSettingsProps,
} from './PositionPickerSettings';

type Item = Position | ServiceContractPosition;

export interface IPositionPickerProps
  extends Pick<
      IPickerProps<Item, false>,
      'value' | 'onChange' | 'maxItems' | 'clearable' | 'placeholder'
    >,
    PositionPickerSettingsProps {
  positions: Item[];
  selectablePositions?: (ErpPositionsKind | ServiceContractPositionKind)[];
  ignoreFinished?: boolean;
}

// This is a trimmed down version of ListEntityPicker with customizations.
export function PositionPicker(props: IPositionPickerProps) {
  const {
    positions,
    descriptionField,
    onDescriptionFieldChange,
    selectablePositions,
    ignoreFinished,
    ...otherProps
  } = props;

  const isInteractive = useCallback(
    (item: Item) => {
      // Check if position is finished (only Position entities have isFinished property)
      const isFinished = 'isFinished' in item ? item.isFinished : false;
      if (isFinished && !ignoreFinished) {
        return false;
      }

      return (
        selectablePositions?.includes(item.positionKind) ??
        SELECTABLE_POSITION_TYPES.includes(item.positionKind)
      );
    },
    [selectablePositions, ignoreFinished]
  );

  const items = useMemo(() => {
    return positions.map((el) => {
      return { ...el, unselectable: !isInteractive(el) };
    });
  }, [isInteractive, positions]);

  function renderRow(position: Item, all: Item[]) {
    const interactiveEl = isInteractive(position);
    const isFinished =
      !ignoreFinished && 'isFinished' in position ? position.isFinished : false;
    const description =
      position[descriptionField] || position.longtext || position.shortText;
    const unit =
      (position as Position).unit ??
      (position as ServiceContractPosition)?.article?.unit?.name;

    // Apply finished styling to all text elements
    const getClassName = (baseClass: string) =>
      isFinished ? `${baseClass} ${styles.finished}` : baseClass;

    const amount =
      (position as ServiceContractPosition).amountDecimal ?? position.amount;

    return (
      <ItemRow
        key={position.id}
        title={description}
        isInteractive={interactiveEl}
        prevWasInteractive={isInteractive(
          all[Math.max(1, all.indexOf(position)) - 1]
        )}
        content={
          <>
            <Typography
              variant={interactiveEl ? 'body1' : 'h4'}
              className={getClassName(styles.mark)}
            />
            <Typography
              variant={interactiveEl ? 'body1' : 'h4'}
              className={getClassName(styles.number)}
            >
              {position.number}
            </Typography>
            <Typography
              variant={interactiveEl ? 'body1' : 'h4'}
              className={getClassName(styles.text)}
            >
              {description}
            </Typography>
            <Typography
              variant={interactiveEl ? 'body1' : 'h4'}
              className={getClassName(styles.amount)}
            >
              {isInteractive ? amount : null}
            </Typography>
            <Typography
              variant={interactiveEl ? 'body1' : 'h4'}
              className={getClassName(styles.unit)}
            >
              {isInteractive ? unit : null}
            </Typography>
          </>
        }
      />
    );
  }

  return (
    <Picker
      options={items}
      sortBy={'longtext'}
      sortByDirection={SortDirection.ASCENDING}
      filterBy={['longtext', 'shortText', 'number']}
      prependSelected={false}
      prependList={
        <PositionPickerHeaderRow descriptionField={descriptionField} />
      }
      renderItemContent={renderRow}
      alignClearIcon={true}
      layout={'advanced'}
      headerAppend={
        <PositionPickerSettings
          descriptionField={descriptionField}
          onDescriptionFieldChange={onDescriptionFieldChange}
        />
      }
      {...otherProps}
    />
  );
}

const SELECTABLE_POSITION_TYPES: (
  | ErpPositionsKind
  | ServiceContractPositionKind
)[] = [
  ErpPositionsKind.STANDARD,
  ErpPositionsKind.STUECKLISTE,
  ErpPositionsKind.INTERNE_POSITION,
  ErpPositionsKind.INTERNE_STUECKLISTE,
  ServiceContractPositionKind.UEBERNEHMEN_MIT_WARTUNGSSUMME,
  ServiceContractPositionKind.UEBERNEHMEN_MIT_E_PREIS_AUS_WARTUNGS_OBJEKT,
  ServiceContractPositionKind.UEBERNEHMEN_OHNE_PREIS,
  ServiceContractPositionKind.UEBERNEHMEN_MIT_ARTIKELPREIS,
  ServiceContractPositionKind.NICHT_UEBERNEHMEN,
];

function PositionPickerHeaderRow({
  descriptionField,
}: Required<Pick<PositionPickerSettingsProps, 'descriptionField'>>) {
  const { t } = useTranslation();

  const context = usePickerContext();

  return (
    <HeaderRow
      content={
        <>
          <Typography variant="h4" className={styles.mark} />
          <Typography variant="h4" className={styles.number}>
            {t('POSITIONS_PICKER.COLUMNS.NUMBER')}
          </Typography>
          <Typography variant="h4" className={styles.text}>
            {t(`POSITIONS_PICKER.COLUMNS.${descriptionField}`)}
          </Typography>
          <Typography variant="h4" className={styles.amount}>
            {t('POSITIONS_PICKER.COLUMNS.AMOUNT')}
          </Typography>
          <Typography variant="h4" className={styles.unit}>
            {t('POSITIONS_PICKER.COLUMNS.UNIT')}
          </Typography>
          {context.hasSelection && <div className={styles.close} />}
        </>
      }
    />
  );
}
